import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OPPORTUNITY_KEYS } from './useOpportunities';
import multiItemEstimationService, {
  CreateCategoryRequest,
  CreateLineItemRequest,
  CostingType
} from '../services/multi-item-estimation.service';
import { message } from 'antd';

// Multi-item estimation hooks
export const useMultiItemEstimation = (estimationId: string) => {
  return useQuery({
    queryKey: ['multiItemEstimation', estimationId],
    queryFn: () => multiItemEstimationService.getMultiItemEstimation(estimationId),
    enabled: !!estimationId && estimationId !== 'new'
  });
};

export const useRecalculateEstimation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (estimationId: string) =>
      multiItemEstimationService.recalculateEstimation(estimationId),
    onSuccess: (data, estimationId) => {
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation', estimationId] });
      message.success('Estimation recalculated successfully');
    },
    onError: () => {
      message.error('Failed to recalculate estimation');
    }
  });
};

// Category hooks
export const useCategories = (estimationId: string) => {
  return useQuery({
    queryKey: ['categories', estimationId],
    queryFn: () => multiItemEstimationService.getCategories(estimationId),
    enabled: !!estimationId
  });
};

export const useAddCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ estimationId, data }: { estimationId: string; data: CreateCategoryRequest }) =>
      multiItemEstimationService.addCategory(estimationId, data),
    onSuccess: (_, { estimationId }) => {
      queryClient.invalidateQueries({ queryKey: ['categories', estimationId] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation', estimationId] });
      message.success('Category added successfully');
    },
    onError: () => {
      message.error('Failed to add category');
    }
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, data }: { categoryId: string; data: Partial<CreateCategoryRequest> }) =>
      multiItemEstimationService.updateCategory(categoryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      message.success('Category updated successfully');
    },
    onError: () => {
      message.error('Failed to update category');
    }
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, deleteLineItems }: { categoryId: string; deleteLineItems?: boolean }) =>
      multiItemEstimationService.deleteCategory(categoryId, deleteLineItems),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      message.success('Category deleted successfully');
    },
    onError: () => {
      message.error('Failed to delete category');
    }
  });
};

// Line item hooks
export const useLineItems = (estimationId: string, categoryId?: string) => {
  return useQuery({
    queryKey: ['lineItems', estimationId, categoryId],
    queryFn: () => multiItemEstimationService.getLineItems(estimationId, categoryId),
    enabled: !!estimationId
  });
};

export const useAddLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ estimationId, data }: { estimationId: string; data: CreateLineItemRequest }) =>
      multiItemEstimationService.addLineItem(estimationId, data),
    onSuccess: (_, { estimationId }) => {
      queryClient.invalidateQueries({ queryKey: ['lineItems', estimationId] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation', estimationId] });
      message.success('Line item added successfully');
    },
    onError: () => {
      message.error('Failed to add line item');
    }
  });
};

export const useUpdateLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, data }: { lineItemId: string; data: Partial<CreateLineItemRequest> }) =>
      multiItemEstimationService.updateLineItem(lineItemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      message.success('Line item updated successfully');
    },
    onError: () => {
      message.error('Failed to update line item');
    }
  });
};

export const useDeleteLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lineItemId: string) => multiItemEstimationService.deleteLineItem(lineItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      message.success('Line item deleted successfully');
    },
    onError: () => {
      message.error('Failed to delete line item');
    }
  });
};

export const useCalculateDetailedCosting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lineItemId: string) => multiItemEstimationService.calculateDetailedCosting(lineItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      message.success('Costing calculated successfully');
    },
    onError: () => {
      message.error('Failed to calculate costing');
    }
  });
};

export const useChangeCostingType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, costingType }: { lineItemId: string; costingType: CostingType }) =>
      multiItemEstimationService.changeCostingType(lineItemId, costingType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      message.success('Costing type changed successfully');
    },
    onError: () => {
      message.error('Failed to change costing type');
    }
  });
};

export const useMoveLineItemToCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, categoryId }: { lineItemId: string; categoryId?: string }) =>
      multiItemEstimationService.moveLineItemToCategory(lineItemId, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      message.success('Line item moved successfully');
    },
    onError: () => {
      message.error('Failed to move line item');
    }
  });
};

export const useCreateEstimationFromBOQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { boqId: string; opportunityId?: string }) =>
      multiItemEstimationService.createFromBOQ(vars.boqId, {
        opportunityId: vars.opportunityId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      queryClient.invalidateQueries({ queryKey: ['estimations'] });
      queryClient.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.all });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create estimation from BOQ');
    }
  });
};

export const usePatchEstimationOpportunity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { estimationId: string; opportunity_id: string | null }) =>
      multiItemEstimationService.patchEstimationOpportunity(vars.estimationId, vars.opportunity_id),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation', vars.estimationId] });
      queryClient.invalidateQueries({ queryKey: ['multiItemEstimation'] });
      queryClient.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.all });
      // Opportunity detail + BOQ list: linked estimations for any opportunity
      queryClient.invalidateQueries({
        predicate: (query) => {
          const k = query.queryKey;
          if (!Array.isArray(k) || k.length < 4) return false;
          return k[0] === 'crm' && k[1] === 'opportunities' && k[3] === 'estimations';
        },
      });
      // Estimation dashboard filtered by CRM opportunity
      queryClient.invalidateQueries({ queryKey: ['crm-estimations'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update opportunity link');
    }
  });
};

